import { useEffect, useMemo, useState } from 'react'
import { api } from '@/services/api'
import type { PipelineActivityTemplate } from '@/services/api/cases'

interface UsePipelineActivitiesResult {
  /** All pipeline activity templates, regardless of category. */
  activities: PipelineActivityTemplate[]
  /** Activities in the `extract` category. */
  extractActivities: PipelineActivityTemplate[]
  /** Activities in the `transform` category. */
  transformActivities: PipelineActivityTemplate[]
  /** Activities in the `load` category. */
  loadActivities: PipelineActivityTemplate[]
  /** Whether the initial fetch is still in flight. */
  isLoading: boolean
}

/**
 * Fetches the pipeline activity templates once and groups them by category.
 * Page-specific data loader for the cases pipelines page.
 */
export function usePipelineActivities(): UsePipelineActivitiesResult {
  const [activities, setActivities] = useState<PipelineActivityTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchActivities() {
      try {
        const data = await api.cases.getPipelineActivities()
        if (!cancelled) setActivities(data)
      } catch (error) {
        console.error('Failed to fetch activities:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchActivities()

    return () => {
      cancelled = true
    }
  }, [])

  const extractActivities = useMemo(
    () => activities.filter(a => a.category === 'extract'),
    [activities]
  )
  const transformActivities = useMemo(
    () => activities.filter(a => a.category === 'transform'),
    [activities]
  )
  const loadActivities = useMemo(() => activities.filter(a => a.category === 'load'), [activities])

  return {
    activities,
    extractActivities,
    transformActivities,
    loadActivities,
    isLoading,
  }
}
